import { useCallback, useEffect, useRef, useState } from 'react'
import { useApi } from '../../core/api/client'
import { useEnv } from '../../core/env/EnvContext'
import { useActiveTokens } from '../../core/auth/TokenProvider'
import { EXPECT_AGENT, isAgentRole, roleLabel, roleSatisfies } from '../../core/auth/roles'
import { RoleMismatchBanner, RoleMismatchIcon } from '../../core/auth/RoleHint'
import { COUNTRIES, type CountryCode } from '../../core/env/types'

interface RegRow {
  registrationId: string
  surname: string
  otherNames: string
  customerPhoneNumber: string
  currentStep: string
  percentComplete: number
  type: string // Fulfilled | Submitted | InProgress | …
  dateLastUpdated: string
}
interface FulfilmentStep { type: string; status: string; completedAt: string | null }
interface FulfilmentDetail {
  reference: string; amount: string; currency: string; customerName: string
  status: string; completedSteps: number; totalSteps: number
  steps: FulfilmentStep[]
  depositPaid: boolean; screened: boolean; accountCreated: boolean; deviceAllocated: boolean
}
interface FlowStepOutcome { label: string; ok: boolean; detail: string }
interface FlowResult { registrationId: string; passed: number; total: number; steps: FlowStepOutcome[] }
interface RegState { registrationId: string; country: CountryCode; firstName: string; lastName: string; idType: string; idNumber: string; phone: string; [k: string]: unknown }

const POLL_MS = 9000
const STEP_LABELS_FALLBACK: string[] = []

// account_creation → Account, etc. Order matches the fulfilment pipeline.
const STEP_META: { type: string; label: string }[] = [
  { type: 'account_creation', label: 'Account' },
  { type: 'deposit_payment', label: 'Deposit' },
  { type: 'screening_call', label: 'Screening' },
  { type: 'device_allocation', label: 'Device' },
]

function ago(iso: string): string {
  const t = new Date(iso).getTime()
  if (Number.isNaN(t)) return ''
  const s = Math.max(0, Math.floor((Date.now() - t) / 1000))
  if (s < 60) return `${s}s ago`
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}

export function RegistrationView() {
  const api = useApi()
  const env = useEnv()
  const { tokens, activeId, setActiveId, activeToken } = useActiveTokens()
  const tokenId = activeToken?.id
  // Expired tokens stay usable: the backend auto-refreshes them on use (refresh
  // grant → re-mint), so we only gate on having a token selected and surface
  // expiry as a non-blocking hint. If a refresh genuinely can't happen, the
  // action call returns the existing "token expired" error, shown inline.
  const hasToken = !!activeToken
  const tokenExpired = !!activeToken && activeToken.expired

  // Preselect a live sales-agent token when entering this screen — registration
  // is a sales-agent action, so default to one (once) if the active token isn't.
  // We never fight a manual change afterwards, and leave the warning to flag it
  // when no agent token is available.
  const autoPicked = useRef(false)
  useEffect(() => {
    if (autoPicked.current || tokens.length === 0) return
    autoPicked.current = true
    if (roleSatisfies(EXPECT_AGENT, activeToken?.role)) return
    // Prefer a live agent token, but fall back to an expired one — it auto-refreshes on use.
    const agentTok = tokens.find((t) => !t.expired && isAgentRole(t.role)) ?? tokens.find((t) => isAgentRole(t.role))
    if (agentTok) setActiveId(agentTok.id)
  }, [tokens, activeToken, setActiveId])

  const [country, setCountry] = useState<CountryCode>(env.country)
  const [error, setError] = useState<string | null>(null)

  // registrations list
  const [regs, setRegs] = useState<RegRow[] | null>(null)
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [details, setDetails] = useState<Record<string, FulfilmentDetail>>({})
  const [rowBusy, setRowBusy] = useState<Record<string, 'load' | 'pay' | 'screen' | 'resume' | null>>({})
  const [rowMsg, setRowMsg] = useState<Record<string, { ok: boolean; text: string } | undefined>>({})
  // Chosen pay amount per row; absent = full deposit. Kept across detail
  // refreshes so a partial payment can be repeated without re-dragging.
  const [payAmounts, setPayAmounts] = useState<Record<string, number>>({})

  // full-flow create
  const [flowBusy, setFlowBusy] = useState(false)
  const [flow, setFlow] = useState<FlowResult | null>(null)

  // step-by-step create
  const [stepLabels, setStepLabels] = useState<string[]>(STEP_LABELS_FALLBACK)
  const [regState, setRegState] = useState<RegState | null>(null)
  const [stepResults, setStepResults] = useState<Record<number, { ok: boolean; detail: string }>>({})
  const [runningStep, setRunningStep] = useState<number | null>(null)
  const stepperRef = useRef<HTMLDivElement | null>(null)

  const loadList = useCallback(async (quiet = false) => {
    if (!tokenId) return
    try {
      const r = await api.get<{ registrations: RegRow[] }>(`/m/registration/search?tokenId=${tokenId}`, { quiet })
      setRegs(r.registrations ?? [])
    } catch (e) {
      if (!quiet) setError(e instanceof Error ? e.message : String(e))
    }
  }, [api, tokenId])

  useEffect(() => { void loadList() }, [loadList])

  // Auto-reload the list quietly (no Activity-panel noise), paused while a flow runs.
  useEffect(() => {
    if (!tokenId) return
    const id = setInterval(() => { if (!flowBusy) void loadList(true) }, POLL_MS)
    return () => clearInterval(id)
  }, [tokenId, flowBusy, loadList])

  useEffect(() => {
    api.get<{ steps: string[] }>('/m/registration/steps').then((r) => setStepLabels(r.steps)).catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function loadDetail(id: string) {
    if (!tokenId) return
    setRowBusy((b) => ({ ...b, [id]: 'load' }))
    try {
      const d = await api.get<FulfilmentDetail>(`/m/registration/fulfillment?tokenId=${tokenId}&registrationId=${id}`)
      setDetails((s) => ({ ...s, [id]: d }))
    } catch (e) {
      setRowMsg((m) => ({ ...m, [id]: { ok: false, text: e instanceof Error ? e.message : String(e) } }))
    } finally {
      setRowBusy((b) => ({ ...b, [id]: null }))
    }
  }

  function toggleRow(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) { next.delete(id); return next }
      next.add(id)
      if (!details[id]) void loadDetail(id)
      return next
    })
  }

  async function rowAction(id: string, action: 'pay' | 'screen', amount?: string) {
    if (!tokenId) return
    setRowBusy((b) => ({ ...b, [id]: action }))
    setRowMsg((m) => ({ ...m, [id]: undefined }))
    const path = action === 'pay' ? '/m/registration/pay-deposit' : '/m/registration/screening'
    try {
      const r = await api.post<{ ok: boolean; detail: string }>(path, { tokenId, registrationId: id, ...(amount ? { amount } : {}) })
      setRowMsg((m) => ({ ...m, [id]: { ok: r.ok, text: r.detail } }))
      await loadDetail(id)
      void loadList(true)
    } catch (e) {
      setRowMsg((m) => ({ ...m, [id]: { ok: false, text: e instanceof Error ? e.message : String(e) } }))
    } finally {
      setRowBusy((b) => ({ ...b, [id]: null }))
    }
  }

  // Load an existing registration back into the stepper to continue it.
  async function resumeRow(id: string) {
    if (!tokenId) return
    setRowBusy((b) => ({ ...b, [id]: 'resume' }))
    setRowMsg((m) => ({ ...m, [id]: undefined }))
    try {
      const r = await api.post<{ state: RegState }>('/m/registration/resume', { tokenId, registrationId: id })
      setRegState(r.state)
      setStepResults({})
      setRunningStep(null)
      setTimeout(() => stepperRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 60)
    } catch (e) {
      setRowMsg((m) => ({ ...m, [id]: { ok: false, text: e instanceof Error ? e.message : String(e) } }))
    } finally {
      setRowBusy((b) => ({ ...b, [id]: null }))
    }
  }

  async function copy(text: string) {
    try { await navigator.clipboard.writeText(text) } catch { /* clipboard blocked */ }
  }

  async function runFlow() {
    if (!tokenId) return
    setFlowBusy(true); setError(null); setFlow(null)
    try {
      const r = await api.streamPost<FlowResult>('/m/registration/run-flow', { tokenId, country })
      setFlow(r)
      await loadList()
      setExpanded((p) => new Set(p).add(r.registrationId))
      void loadDetail(r.registrationId)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setFlowBusy(false)
    }
  }

  async function newRegistration() {
    if (!hasToken) return
    setError(null); setStepResults({}); setRunningStep(null)
    try {
      const r = await api.post<{ state: RegState }>('/m/registration/new', { country })
      setRegState(r.state)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  async function runOneStep(index: number) {
    if (!tokenId || !regState) return
    setRunningStep(index); setError(null)
    try {
      const r = await api.streamPost<{ result: { ok: boolean; detail: string }; state: RegState }>(
        '/m/registration/step', { tokenId, stepIndex: index, state: regState },
      )
      setRegState(r.state)
      setStepResults((prev) => ({ ...prev, [index]: { ok: r.result.ok, detail: r.result.detail } }))
      void loadList(true)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setRunningStep(null)
    }
  }

  return (
    <div className="module">
      <h2>Customer Registration</h2>
      <p className="muted">Create registrations and drive each one to completion — pay the deposit and complete screening, right from the list.</p>

      {error && <div className="banner error">{error}</div>}

      {/* Active token */}
      <section>
        <h3>Acting as</h3>
        {tokens.length === 0 ? (
          <p className="muted">No tokens yet — mint one in <strong>Token Minter</strong> first.</p>
        ) : (
          <label className="field">
            <span className="field-label">Token</span>
            <div className="row" style={{ gap: 8, alignItems: 'center' }}>
              <select value={activeId ?? ''} onChange={(e) => setActiveId(e.target.value || null)}>
                {tokens.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.friendly_name} · {roleLabel(t.role)} · {t.environment} · {t.expired ? 'expired' : 'live'}
                  </option>
                ))}
              </select>
              <RoleMismatchIcon exp={EXPECT_AGENT} role={activeToken?.role} />
            </div>
          </label>
        )}
        {tokenExpired && <div className="banner warning" style={{ marginTop: 12, marginBottom: 0 }}>Selected token is expired — it’ll auto-refresh when you run an action (or refresh it in Token Minter).</div>}
        {hasToken && <RoleMismatchBanner exp={EXPECT_AGENT} role={activeToken?.role} />}
      </section>

      {/* Create */}
      <section>
        <h3>Create</h3>
        <div className="create-bar">
          <label className="field">
            <span className="field-label">Country</span>
            <select value={country} onChange={(e) => setCountry(e.target.value as CountryCode)}>
              {COUNTRIES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>
          <button disabled={!hasToken || flowBusy} onClick={runFlow}>{flowBusy ? 'Running 17 steps…' : 'Run full flow'}</button>
          <button className="ghost" disabled={!hasToken} onClick={newRegistration}>New registration (step-by-step)</button>
        </div>
        {flowBusy && <p className="muted" style={{ marginTop: 10 }}>~25 sequential API calls — watch the Activity readout.</p>}
        {flow && (
          <div className="banner success" style={{ marginTop: 12 }}>
            <strong>{flow.passed}/{flow.total} steps passed</strong> · <code>{flow.registrationId}</code>
            <button className="ghost" style={{ marginLeft: 8 }} onClick={() => copy(flow.registrationId)}>Copy</button>
          </div>
        )}

        {regState && (
          <div className="stepper" ref={stepperRef}>
            <div className="row spread" style={{ marginBottom: 6 }}>
              <span className="muted">Stepping <code>{regState.registrationId}</code> — {regState.firstName} {regState.lastName}</span>
              <span className="row">
                <button className="ghost" onClick={() => copy(regState.registrationId)}>Copy ID</button>
                <button className="ghost" onClick={() => { setRegState(null); setStepResults({}); setRunningStep(null) }}>Close</button>
              </span>
            </div>
            <table className="steps">
              <tbody>
                {stepLabels.map((label, i) => {
                  const res = stepResults[i]; const running = runningStep === i
                  return (
                    <tr key={i} className={res ? (res.ok ? 'step-ok' : 'step-fail') : ''}>
                      <td>{i + 1}</td>
                      <td>{running ? '…' : res ? (res.ok ? '✓' : '✗') : ''}</td>
                      <td>{label}</td>
                      <td className="muted">{res?.detail}</td>
                      <td style={{ width: 56 }}>
                        <button className="ghost" disabled={!hasToken || runningStep !== null} onClick={() => runOneStep(i)}>{running ? 'Running' : 'Run'}</button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Registrations — the hero */}
      <section>
        <div className="row spread" style={{ alignItems: 'baseline' }}>
          <h3 style={{ border: 0, margin: 0, padding: 0 }}>Registrations</h3>
          <span className="muted reg-autonote">auto-refreshing · <button className="linklike" onClick={() => loadList()}>refresh now</button></span>
        </div>

        {!hasToken && <p className="muted" style={{ marginTop: 14 }}>Select a token to load registrations.</p>}
        {hasToken && regs === null && <p className="muted" style={{ marginTop: 14 }}>Loading…</p>}
        {hasToken && regs?.length === 0 && <p className="muted" style={{ marginTop: 14 }}>No registrations yet. Run a flow above to create one.</p>}

        {regs && regs.length > 0 && (
          <ul className="reg-list">
            {regs.map((r) => {
              const open = expanded.has(r.registrationId)
              const d = details[r.registrationId]
              const busy = rowBusy[r.registrationId]
              const msg = rowMsg[r.registrationId]
              const name = `${r.otherNames ?? ''} ${r.surname ?? ''}`.trim() || '—'
              const fullAmount = d ? Math.floor(parseFloat(d.amount)) : NaN
              const canSlide = Number.isFinite(fullAmount) && fullAmount > 1
              const payAmount = Math.min(payAmounts[r.registrationId] ?? fullAmount, fullAmount)
              const partial = canSlide && payAmount < fullAmount
              return (
                <li key={r.registrationId} className="reg">
                  <button className="reg-head" onClick={() => toggleRow(r.registrationId)} aria-expanded={open}>
                    <span className="reg-caret">{open ? '▾' : '▸'}</span>
                    <span className="reg-name">{name}</span>
                    <span className="reg-phone">{r.customerPhoneNumber}</span>
                    <span className={`reg-type t-${r.type?.toLowerCase()}`}>{r.type}</span>
                    <span className="reg-pct">{Math.round((r.percentComplete ?? 0) * 100)}%</span>
                    <span className="reg-ago">{ago(r.dateLastUpdated)}</span>
                  </button>

                  {open && (
                    <div className="reg-detail">
                      <div className="row spread reg-idrow">
                        <code>{r.registrationId}</code>
                        <button className="ghost" onClick={() => copy(r.registrationId)}>Copy ID</button>
                      </div>

                      {busy === 'load' && !d && <p className="muted">Loading fulfilment…</p>}

                      {d && (
                        <>
                          <div className="chips">
                            {STEP_META.map((s) => {
                              const st = d.steps.find((x) => x.type === s.type)?.status
                              const done = st === 'succeeded'
                              return <span key={s.type} className={`chip ${done ? 'on' : ''}`}>{done ? '✓' : '○'} {s.label}</span>
                            })}
                          </div>
                          <p className="muted reg-deposit">
                            Deposit <code>{d.reference || '—'}</code> · {d.amount || '—'} {d.currency}
                            {d.depositPaid ? <span className="tag live" style={{ marginLeft: 8 }}>paid</span> : <span className="tag expired" style={{ marginLeft: 8 }}>due</span>}
                          </p>
                        </>
                      )}

                      {/* Actions render even with no fulfilment record yet (an in-progress
                          registration has none until it's submitted), so there's always a
                          way to continue it. */}
                      {d && !d.depositPaid && canSlide && (
                        <div className="reg-pay">
                          <span className="field-label">Amount</span>
                          <input
                            type="range" min={1} max={fullAmount} step={1} value={payAmount} disabled={!!busy}
                            onChange={(e) => setPayAmounts((p) => ({ ...p, [r.registrationId]: Number(e.target.value) }))}
                          />
                          <span className={`reg-pay-val ${partial ? 'partial' : ''}`}>
                            {payAmount.toLocaleString()} / {fullAmount.toLocaleString()} {d.currency}
                          </span>
                        </div>
                      )}

                      <div className="reg-actions">
                        {d && !d.depositPaid && (
                          <button disabled={!hasToken || !!busy} onClick={() => rowAction(r.registrationId, 'pay', canSlide ? String(payAmount) : undefined)}>
                            {busy === 'pay' ? 'Paying…' : partial ? `Pay ${payAmount.toLocaleString()} (partial)` : 'Pay deposit'}
                          </button>
                        )}
                        {d && !d.screened && (
                          <button disabled={!hasToken || !!busy} onClick={() => rowAction(r.registrationId, 'screen')}>
                            {busy === 'screen' ? 'Screening…' : 'Complete screening'}
                          </button>
                        )}
                        {d && d.depositPaid && d.screened && d.deviceAllocated && <span className="reg-done">✓ Fully fulfilled</span>}
                        <button className="ghost" disabled={!hasToken || !!busy} onClick={() => resumeRow(r.registrationId)}>
                          {busy === 'resume' ? 'Loading…' : 'Continue stepping'}
                        </button>
                        <button className="ghost" disabled={!!busy} onClick={() => loadDetail(r.registrationId)}>Refresh</button>
                      </div>

                      {msg && <div className={`banner ${msg.ok ? 'success' : 'error'}`} style={{ marginTop: 10, marginBottom: 0 }}>{msg.text}</div>}
                    </div>
                  )}
                </li>
              )
            })}
          </ul>
        )}
      </section>
    </div>
  )
}
